import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { RhButton, RhButtonMode } from '../RhButton';
import { RhCard } from '../RhCard';
import { SizedBox } from '../SizedBox';
import { H2, Regular } from '../Styles';

type Props = {
  initialValue: string[];
  onChange: (categories: Category[]) => void;
};

type Category =
  | 'discipleship'
  | 'books-of-the-bible'
  | 'christian-books'
  | 'by-topic'
  | 'live-streaming'
  | 'women'
  | 'men'
  | 'teens'
  | 'children'
  | 'couples'
  | 'parents'
  | 'mothers'
  | 'singles'
  | 'catholic'
  | 'spanish';

type FormState = {
  [key in Category]?: boolean;
};

const options: { label: string; options: { value: Category; label: string }[] }[] = [
  {
    label: 'frequent',
    options: [
      { value: 'discipleship', label: 'Discipleship' },
      { value: 'books-of-the-bible', label: 'Books of the Bible' },
      { value: 'christian-books', label: 'Christian Books' },
      { value: 'by-topic', label: 'By Topic' },
      { value: 'live-streaming', label: 'Live Streaming' },
    ],
  },
  {
    label: 'demographic',
    options: [
      { value: 'women', label: 'Women' },
      { value: 'men', label: 'Men' },
      { value: 'teens', label: 'Teens' },
      { value: 'children', label: 'Children' },
      { value: 'couples', label: 'Couples' },
      { value: 'parents', label: 'Parents' },
      { value: 'mothers', label: 'Mothers' },
      { value: 'singles', label: 'Singles' },
    ],
  },
  {
    label: 'language',
    options: [
      { value: 'catholic', label: 'Catholic' },
      { value: 'spanish', label: 'Spanish' },
    ],
  },
];

function arrayize(obj: FormState) {
  const categories: Category[] = [];
  Object.keys(obj).forEach(key => {
    const category = key as Category;
    if (obj[category]) {
      categories.push(category);
    }
  });
  return categories;
}

function objectize(arr: Category[]) {
  const lib: FormState = {};
  arr.forEach(category => {
    lib[category] = true;
  });
  return lib;
}

export const CategoryCard: React.FC<Props> = props => {
  const [categories, setCategories] = useState<FormState>({});
  const [isMoreShown, setIsMoreShown] = useState(false);

  useEffect(() => {
    setCategories(objectize(props.initialValue as Category[]));
  }, [props.initialValue]);

  const opts = isMoreShown ? options : options.slice(0, 1);

  return (
    <RhCard mode="tiled">
      <SizedBox height={2} />
      <div className="flex items-center justify-between">
        <H2>Category</H2>
      </div>
      <SizedBox height={12} />
      <div className="flex flex-wrap">
        {opts.map(({ label, options }, i) => {
          return (
            <div
              key={label}
              className={clsx('flex flex-wrap', {
                'border-b mb-3 pb-1': isMoreShown,
              })}>
              {options.map(({ label, value }) => {
                const isActive = categories[value] || false;
                return (
                  <div
                    key={value}
                    className={clsx('cursor-pointer px-2 py-1 rounded-full mr-2 mb-2 border border-primary', {
                      'bg-primary': isActive,
                      'bg-white': !isActive,
                    })}
                    onClick={() => {
                      const newCategories = { ...categories, [value]: !isActive };
                      setCategories(newCategories);
                      props.onChange(arrayize(newCategories));
                    }}>
                    <Regular
                      className={clsx('font-bold whitespace-nowrap', {
                        'text-white': isActive,
                        'text-primary': !isActive,
                      })}>
                      {label}
                    </Regular>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {isMoreShown ? (
        <RhButton onClick={() => setIsMoreShown(false)} title="show less..." mode={RhButtonMode.text} />
      ) : (
        <RhButton onClick={() => setIsMoreShown(true)} title="show more..." mode={RhButtonMode.text} />
      )}
    </RhCard>
  );
};
